import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Trophy, ArrowUpRight } from 'lucide-react';
import api from '../../lib/api';

/**
 * Teaser for /c/top — uses the abstract liquid-gold media as background
 * and pulls #1 producer + #1 DJ live to give the card real content.
 */
export default function TopOfTheWeekTeaser() {
  const [topProducer, setTopProducer] = useState(null);
  const [topDj, setTopDj] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [pRes, dRes] = await Promise.all([
          api.get('/public/producers?limit=20').catch(() => ({ data: [] })),
          api.get('/public/djs?limit=20').catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        const producers = Array.isArray(pRes.data) ? pRes.data : [];
        const djs = Array.isArray(dRes.data) ? dRes.data : [];
        // Same heuristic as the OG image route
        const verified = producers.filter((p) => p.is_verified);
        const pool = verified.length ? verified : producers;
        pool.sort((a, b) => (b.upcoming_events_count || 0) - (a.upcoming_events_count || 0));
        setTopProducer(pool[0] || null);
        setTopDj(djs[0] || null);
      } catch (e) {
        // silent
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0A]"
      data-testid="top-of-the-week-teaser"
    >
      {/* Liquid-gold abstract */}
      <div className="absolute inset-0 opacity-50">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/3e7e6f22-74b0-4df4-a2fb-81cc35d2429a/images/2c7f6f57ab9fadc0bbf9895b79c8e1339189c6d3dab6ebbfa8ca9061a2d0bae6.png')",
          }}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 sm:p-12 lg:p-16">
        {/* Left: title */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <Crown size={12} className="text-[#D4AF37]" />
            <span
              className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#D4AF37]"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              Top de la Semana
            </span>
          </div>
          <h2
            className="text-4xl sm:text-5xl lg:text-7xl font-black text-white tracking-tighter leading-[0.9] mb-5 uppercase"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            La escena
            <br />
            <span className="text-[#D4AF37]">en órbita.</span>
          </h2>
          <p className="text-base sm:text-lg text-white/60 max-w-md leading-relaxed mb-8">
            Cada lunes refrescamos el ranking. Productoras y DJs que mueven la aguja esta semana en Argentina.
          </p>
          <Link
            to="/c/top"
            className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-extrabold uppercase tracking-[0.2em] text-sm px-6 py-3.5 rounded-full hover:bg-[#F5D25A] transition-colors"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
            data-testid="top-of-week-cta"
          >
            Ver el ranking
            <ArrowUpRight size={16} />
          </Link>
        </div>

        {/* Right: stacked #1 cards */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          <RankCard
            label="#1 PRODUCTORA"
            name={topProducer?.name}
            sub={topProducer?.city}
            image={topProducer?.logo}
            href={topProducer ? `/productora/${topProducer.slug}` : null}
            accent={topProducer?.color || '#D4AF37'}
            data-testid="top-producer-card"
          />
          <RankCard
            label="#1 DJ"
            name={topDj?.name}
            sub={(topDj?.genres || []).slice(0, 2).join(' · ')}
            image={topDj?.photo}
            href={topDj ? `/dj/${topDj.slug}` : null}
            accent="#D4AF37"
            data-testid="top-dj-card"
          />
        </div>
      </div>
    </motion.div>
  );
}

function RankCard({ label, name, sub, image, href, accent }) {
  const inner = (
    <div className="group flex items-center gap-4 p-4 rounded-2xl bg-white/[0.04] backdrop-blur-sm border border-white/10 hover:border-[#D4AF37]/40 transition-colors">
      <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex-shrink-0">
        {image ? (
          <img src={image} alt={name || ''} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30">
            <Trophy size={20} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1"
          style={{ color: accent, fontFamily: "'Unbounded', sans-serif" }}
        >
          {label}
        </p>
        <p className="text-lg font-bold text-white truncate" style={{ fontFamily: "'Unbounded', sans-serif" }}>
          {name || '—'}
        </p>
        {sub && <p className="text-xs text-white/40 truncate">{sub}</p>}
      </div>
      {href && (
        <ArrowUpRight size={16} className="text-white/30 group-hover:text-[#D4AF37] transition-colors flex-shrink-0" />
      )}
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}
